// 検索結果の高密度インライン表示（案A）。
// モーラ数グルーピング＋粘着ツールバー（件数/並び替え/ジャンプナビ/凡例）＋クリックでコピー。
// 表示は最大300件なので仮想化しない（ページスクロールに一本化）。

import { useMemo, useState } from 'react'
import { groupByMora, GROUP_MORA_CAP } from './groupResults'
import { VowelHighlight } from './VowelHighlight'
import { SortSelect } from './RefinePanel'
import type { WorkerResult, SortOrder } from '../workers/searchClient'
import type { ComboPair } from './useSearchState'

interface Props {
  results: WorkerResult[]
  total: number
  tookMs: number
  /** クエリ読みのモーラ数。同モーラのグループを先頭に出し、ナビで強調する。 */
  queryMora: number
  /** ユーザーが指定した深さ（フォールバック前）。 */
  requestedDepth: number
  /** 深さの自動フォールバックが発生した場合のみ非null。実際に使った深さ。 */
  usedDepth: number | null
  posLegend: string[]
  combos: ComboPair[]
  sort: SortOrder
  onSort: (s: SortOrder) => void
  onCopy: (text: string) => void
}

function Word({
  r,
  posLegend,
  onCopy,
}: {
  r: WorkerResult
  posLegend: string[]
  onCopy: (t: string) => void
}) {
  const pos = posLegend[r.pos] && posLegend[r.pos] !== 'その他' ? posLegend[r.pos] : null
  const isProper = posLegend[r.pos] === '固有名詞'
  const tier = r.rankTier ?? 1
  // 表記＝読み（カタカナ語）はルビを省略し、本体に直接ハイライトする。
  const isKanaSurface = r.surface === r.reading
  return (
    <button
      type="button"
      data-testid="result-word"
      data-surface={r.surface}
      onClick={() => onCopy(r.surface)}
      title={pos ? `${pos}・クリックでコピー` : 'クリックでコピー'}
      aria-label={`${r.surface} をコピー`}
      className={`rounded-md px-1 py-0.5 text-left leading-snug transition hover:bg-accent-vio/10 active:scale-95 ${
        tier === 0 ? 'font-bold' : tier === 2 ? 'opacity-55' : ''
      }`}
    >
      {isKanaSurface ? (
        <span className={`text-[15px] ${isProper ? 'text-proper' : ''}`}>
          <VowelHighlight
            reading={r.reading}
            matchStart={r.matchStart}
            matchEnd={r.matchEnd}
            missMorae={r.missMorae}
            tone={isProper ? 'proper' : 'default'}
          />
        </span>
      ) : (
        <ruby className={`text-[15px] ${isProper ? 'text-proper' : 'text-ink'}`}>
          {r.surface}
          <rt className="text-[9px]">
            <VowelHighlight
              reading={r.reading}
              matchStart={r.matchStart}
              matchEnd={r.matchEnd}
              missMorae={r.missMorae}
            />
          </rt>
        </ruby>
      )}
    </button>
  )
}

/** 異体（表記ゆれ）の展開ボタン。読みは代表表記と共通なのでルビなしでコピー用ボタンを並べる。 */
function VariantWord({ surface, onCopy }: { surface: string; onCopy: (t: string) => void }) {
  return (
    <button
      type="button"
      data-testid="variant-word"
      onClick={() => onCopy(surface)}
      title="クリックでコピー"
      aria-label={`${surface} をコピー`}
      className="rounded-md px-1 py-0.5 text-left text-[15px] leading-snug text-ink transition hover:bg-accent-vio/10 active:scale-95"
    >
      {surface}
    </button>
  )
}

/** 語＋（異体があれば）展開バッジ・展開済み異体一覧。 */
function WordEntry({
  r,
  posLegend,
  onCopy,
  expanded,
  onToggle,
}: {
  r: WorkerResult
  posLegend: string[]
  onCopy: (t: string) => void
  expanded: boolean
  onToggle: (id: number) => void
}) {
  const variants = r.variants ?? []
  return (
    <>
      <span className="inline-flex items-center gap-0.5">
        <Word r={r} posLegend={posLegend} onCopy={onCopy} />
        {variants.length > 0 && (
          <button
            type="button"
            onClick={() => onToggle(r.id)}
            aria-expanded={expanded}
            aria-label={`表記ゆれを${variants.length}件表示`}
            className="rounded-full border border-line bg-paper px-1 text-[10px] font-bold leading-tight text-mut transition hover:border-mut/50"
          >
            +{variants.length}
          </button>
        )}
      </span>
      {variants.length > 0 && expanded && (
        <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-1 rounded-md bg-paper px-1.5 py-0.5">
          {variants.map((v, i) => (
            <VariantWord key={`${r.id}-${i}`} surface={v} onCopy={onCopy} />
          ))}
        </span>
      )}
    </>
  )
}

/** 2語コンボ提案セクション。ツールバー直下・最初のモーラグループの前に表示する。 */
function ComboSection({ combos, onCopy }: { combos: ComboPair[]; onCopy: (t: string) => void }) {
  if (combos.length === 0) return null
  return (
    <section className="mt-4">
      <h2 className="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <b className="text-lg text-ink">2語コンボ</b>
        <span className="text-[11px] text-mut">ぜんぶの母音で踏める2語の組み合わせ</span>
      </h2>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {combos.map((c, i) => (
          <button
            key={`${c.leftSurface}-${c.rightSurface}-${i}`}
            type="button"
            data-testid="combo-pair"
            onClick={() => onCopy(`${c.leftSurface} ${c.rightSurface}`)}
            title="クリックでコピー"
            aria-label={`${c.leftSurface} ${c.rightSurface} をコピー`}
            className="rounded-md px-1 py-0.5 text-left leading-snug transition hover:bg-accent-vio/10 active:scale-95"
          >
            <ruby className="text-[15px] text-ink">
              {c.leftSurface}
              {c.rightSurface}
              <rt className="text-[9px]">
                <span className="rounded-[3px] bg-gradient-to-r from-hl-from to-hl-to font-bold text-ink">
                  {c.leftReading}・{c.rightReading}
                </span>
              </rt>
            </ruby>
          </button>
        ))}
      </div>
    </section>
  )
}

export function ResultFlow({
  results,
  total,
  tookMs,
  queryMora,
  requestedDepth,
  usedDepth,
  posLegend,
  combos,
  sort,
  onSort,
  onCopy,
}: Props) {
  const groups = useMemo(() => groupByMora(results, queryMora), [results, queryMora])
  const queryGroup = Math.min(queryMora, GROUP_MORA_CAP)
  // 異体（表記ゆれ）の展開状態。語 id ごとにローカルで開閉する。
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const toggleVariant = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const jumpTo = (label: string) => {
    document
      .getElementById(`mora-${label}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div>
      {/* 粘着ツールバー: 件数・並び替え・モーラジャンプ・凡例を1行に */}
      <div className="sticky top-0 z-10 -mx-4 flex items-center gap-3 overflow-x-auto border-b border-line bg-paper/95 px-4 py-2 text-xs text-mut backdrop-blur">
        <span data-testid="total" className="shrink-0 whitespace-nowrap">
          <span className="font-bold text-ink tabular-nums">{total.toLocaleString()}</span> 件
          {total > results.length && <span className="ml-1">（上位 {results.length} 件）</span>}
          <span className="ml-2">{tookMs.toFixed(1)} ms</span>
        </span>
        {usedDepth != null && (
          <span data-testid="fallback-note" className="shrink-0 whitespace-nowrap font-semibold text-accent-pink">
            {requestedDepth}母音は0件 → {usedDepth}母音で表示中
          </span>
        )}
        <SortSelect sort={sort} onSort={onSort} />
        <nav className="flex shrink-0 items-center gap-1" aria-label="モーラ数へ移動">
          <span className="mr-0.5">モーラ</span>
          {groups.map((g) => (
            <button
              key={g.label}
              type="button"
              onClick={() => jumpTo(g.label)}
              className={`rounded-md border px-2 py-0.5 font-bold tabular-nums transition ${
                g.mora === queryGroup
                  ? 'border-transparent bg-gradient-to-r from-accent-pink to-accent-vio text-white'
                  : 'border-line bg-card text-ink hover:border-mut/50'
              }`}
            >
              {g.label}
            </button>
          ))}
        </nav>
        <span className="ml-auto hidden shrink-0 whitespace-nowrap sm:inline">
          <em className="rounded-[3px] bg-gradient-to-r from-hl-from to-hl-to px-1 font-bold not-italic text-ink">
            一致
          </em>
          ／
          <span className="text-miss underline decoration-miss decoration-dashed decoration-2 underline-offset-2">
            ずれ
          </span>
          ・よく使う語は太字・
          <span className="text-proper">固有名詞</span>は青字
        </span>
      </div>

      <ComboSection combos={combos} onCopy={onCopy} />

      {groups.map((g) => (
        <section key={g.label} id={`mora-${g.label}`} className="scroll-mt-12">
          <h2 className="mb-1.5 mt-5 flex items-center gap-2">
            <b className="text-lg text-ink tabular-nums">{g.label}</b>
            <span className="text-[11px] tracking-wider text-mut">モーラ</span>
            <span className="h-px flex-1 bg-line" aria-hidden />
          </h2>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {g.items.map((r) => (
              <WordEntry
                key={r.id}
                r={r}
                posLegend={posLegend}
                onCopy={onCopy}
                expanded={expandedIds.has(r.id)}
                onToggle={toggleVariant}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
